<template>
  <span v-if="props.anchor" :id="props.anchor" class="anchor"></span>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between bg-primary">
      <h4 class="text-uppercase mb-0">Hôtels</h4>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addHotel()"><i class="fa-solid fa-plus pe-1"></i>Ajouter un hôtel</button>
    </div>
    <div class="card-body">
      <div v-if="hotels.length === 0" class="text-muted">
        Aucun hôtel
      </div>
      <div v-else v-for="(hotel,idx) in hotels" :key="hotel.key" class="card mb-3">
        <span :id="`hotels[${idx}]`" class="anchor"></span>
        <div class="card-header d-flex justify-content-between bg-primary bg-opacity-25">
          <h4 class="text-uppercase mb-0">{{hotel.value?.name.length > 0 ? hotel.value?.name : `Hôtel ${idx + 1}`}}</h4>
          <button class="btn btn-link link-dark p-0" @click="removeHotel(idx)"><i class="fa-regular fa-trash-can fa-lg"></i></button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-9">
              <TextInputComponent
                :value-key="`hotels[${idx}].name`"
                label="Nom de l'hôtel"
                placeholder="Nom de l'hôtel"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-3">
              <NumberInputComponent
                :value-key="`hotels[${idx}].nights`"
                label="Nuitées"
                placeholder="Nuitées"
                :required="true"
                class="mb-3"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <FileUploadComponent
                type="image"
                :value-key="`hotels[${idx}].picture`"
                :required="false"
                class="mb-3"
                height="80px"
                :hide-icon="true"
              />
            </div>
          </div>

          <AdminTravelEditHotelQuotaGroup
            class="mb-3"
            :value-key="`hotels[${idx}].quotaGroups`"
            v-model:validation-schema="quotaGroupsValidationSchema"
          />
        </div>

      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { useFieldArray } from 'vee-validate';
import {IdNotDefined,ScrollToId } from '@/compositions/helper'
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import AdminTravelEditHotelQuotaGroup from '@/views/admin/travel/AdminTravelEditHotelQuotaGroup.vue';

import * as zod from 'zod';

const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });
const quotaGroupsValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} });

const props = defineProps<{
  anchor?: string
}>();

const { remove: removeHotel, push: pushHotel, fields: hotels } = useFieldArray<TravelDbHotel>(() => 'hotels');

const addHotel = () => {
  pushHotel(<TravelDbHotel>{
    id: IdNotDefined,
    name: `Hotel ${(hotels?.value.length ?? 0) + 1}`,
  });
  ScrollToId(`hotels[${(hotels?.value.length ?? 1) - 1}]`);
}

const mergedSchema = computed(() => {
  return {
    shape: {
      hotels: zod.array(zod.object({
        id: zod.number().nonnegative(),
        name: zod.string().min(1, 'Le nom de l\'hôtel est obligatoire'),
        nights: zod.number({message: "Le nombre de nuit est obligatoire"}).min(0, 'Le nombre de nuit doit être au minimum 0'),
        picture: zod.object({
          id: zod.number().nonnegative(),
          key: zod.string().uuid().optional(),
          mimeType: zod.string().optional(),
          base64Content: zod.string().optional()
         }).optional(),
        ...zod.object(quotaGroupsValidationSchema.value.shape).shape,
      })).optional()
    },
    anchors: {
      "hotels[\\d].id": "self",
      "hotels[\\d].name": "self",
      "hotels[\\d].nights": "self",
      "hotels[\\d].picture": "self",
      ...quotaGroupsValidationSchema.value.anchors
    }
  }
});

onMounted(() => {
  validationSchema.value = mergedSchema.value
});

watch(quotaGroupsValidationSchema, async () => {
  validationSchema.value = mergedSchema.value
});
</script>
